/**
 * Shared joint-editing behaviour for every control that moves a joint.
 *
 * Moving a joint is not just "send the new angle": the value has to land in
 * the store immediately so the scene follows the finger, the wire traffic has
 * to be throttled against the server's acknowledgements, and undo has to
 * remember the angle from before the gesture rather than every frame of it.
 * Both the desktop joint list and the mobile joint sheet need all of that, so
 * it lives here instead of inside either one.
 */

import { useCallback, useEffect, useRef } from "react";
import { useJoints, useLastTransformUpdateTime, useViewerStore } from "../../store";
import { useWebSocket } from "../../WebSocket";
import { recordJoint } from "../../hooks";
import { createJointSliderStream, type JointSliderStream } from "./jointSliderStream";

export interface JointSliderControl {
  /** Remember the pre-gesture angle, once per gesture, for undo. */
  beginJointEdit: (jointName: string) => void;
  /** Typed or stepped change: update locally without opening the throttle. */
  changeJointValue: (jointName: string, value: number) => void;
  /** Mid-drag change: update locally and stream the angle to the server. */
  dragJointValue: (jointName: string, value: number) => void;
  /** End of gesture: record undo and flush the final angle. */
  commitJointValue: (jointName: string, value: number) => void;
  /** End of gesture for controls that only wrote to the store (e.g. on blur). */
  commitJointFromStore: (jointName: string) => void;
}

export function useJointSliderControl(): JointSliderControl {
  const joints = useJoints();
  const lastTransformUpdateTime = useLastTransformUpdateTime();
  const updateJointValue = useViewerStore((state) => state.updateJointValue);
  const { sendJointSliderUpdate } = useWebSocket();

  // Angle each joint held when its current gesture started, for undo.
  const jointDragStartValue = useRef<Record<string, number>>({});
  const jointSliderStreams = useRef<Record<string, JointSliderStream>>({});

  const getJointSliderStream = useCallback(
    (jointName: string): JointSliderStream => {
      if (!jointSliderStreams.current[jointName]) {
        jointSliderStreams.current[jointName] = createJointSliderStream({
          send: (value) => sendJointSliderUpdate(jointName, value),
        });
      }
      return jointSliderStreams.current[jointName];
    },
    [sendJointSliderUpdate]
  );

  useEffect(() => {
    for (const stream of Object.values(jointSliderStreams.current)) {
      stream.acknowledge();
    }
  }, [lastTransformUpdateTime]);

  useEffect(() => {
    const streams = jointSliderStreams.current;
    return () => {
      for (const stream of Object.values(streams)) {
        stream.cancel();
      }
    };
  }, []);

  const beginJointEdit = useCallback(
    (jointName: string) => {
      if (jointName in jointDragStartValue.current) {
        return;
      }
      const joint = joints.find((j) => j.name === jointName);
      if (joint) {
        jointDragStartValue.current[jointName] = joint.current_angle;
      }
    },
    [joints]
  );

  const changeJointValue = useCallback(
    (jointName: string, value: number) => {
      beginJointEdit(jointName);
      updateJointValue(jointName, value);
    },
    [beginJointEdit, updateJointValue]
  );

  const dragJointValue = useCallback(
    (jointName: string, value: number) => {
      beginJointEdit(jointName);
      updateJointValue(jointName, value);
      getJointSliderStream(jointName).enqueue(value);
    },
    [beginJointEdit, getJointSliderStream, updateJointValue]
  );

  const commitJointValue = useCallback(
    (jointName: string, value: number) => {
      // Undo restores the angle from before the drag, not the last frame of it.
      const startValue = jointDragStartValue.current[jointName];
      if (startValue !== undefined && startValue !== value) {
        recordJoint(jointName, startValue);
      }
      delete jointDragStartValue.current[jointName];
      const stream = jointSliderStreams.current[jointName];
      if (stream) {
        stream.flushFinal(value);
        return;
      }
      sendJointSliderUpdate(jointName, value);
    },
    [sendJointSliderUpdate]
  );

  const commitJointFromStore = useCallback(
    (jointName: string) => {
      const joint = joints.find((j) => j.name === jointName);
      if (!joint) {
        return;
      }
      commitJointValue(jointName, joint.current_angle);
    },
    [commitJointValue, joints]
  );

  return {
    beginJointEdit,
    changeJointValue,
    dragJointValue,
    commitJointValue,
    commitJointFromStore,
  };
}
